/**
 * useAzureSpeech Hook
 * Handles Azure Speech Service pronunciation assessment
 * Uses Azure Cognitive Services Speech SDK
 *
 * SECURITY: Uses temporary tokens from IssueSpeechToken Azure Function
 * instead of exposing the subscription key in the frontend.
 * Tokens are valid for 10 minutes and cached for 9 minutes.
 */

import { useState, useCallback } from "react";
import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";
import { PronunciationResult } from "@/types/pronunciation.types";
import { logger } from "@/utils/logger";
import { config } from "@/config/env";

interface UseAzureSpeechReturn {
  assessPronunciation: (
    pushStream: SpeechSDK.PushAudioInputStream,
    referenceText: string
  ) => Promise<PronunciationResult>;
  isProcessing: boolean;
  error: string | null;
  clearError: () => void;
}

/**
 * Speech token response from IssueSpeechToken Azure Function
 */
interface SpeechTokenResponse {
  token: string;
  region: string;
  expiresIn: number;
}

/**
 * Cache for Speech token (avoid fetching on every assessment)
 * Token is valid for 10 minutes, we cache for 9 minutes to be safe
 */
let tokenCache: {
  token: string;
  region: string;
  expiresAt: number;
} | null = null;

const TOKEN_CACHE_DURATION_MS = 9 * 60 * 1000; // 9 minutes (token lasts 10)

/**
 * Fetch temporary Speech token from Azure Function
 * This keeps the subscription key secure on the server side
 */
const getSpeechToken = async (): Promise<{ token: string; region: string }> => {
  const now = Date.now();

  // Return cached token if still valid
  if (tokenCache && tokenCache.expiresAt > now) {
    logger.log("[useAzureSpeech] Using cached token");
    return { token: tokenCache.token, region: tokenCache.region };
  }

  logger.log(
    "[useAzureSpeech] Fetching new Speech token from Azure Function..."
  );

  try {
    const response = await fetch(
      `${config.azureFunctionsUrl}/issue-speech-token`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Failed to fetch token: ${response.status}`
      );
    }

    const data: SpeechTokenResponse = await response.json();

    if (!data.token || !data.region) {
      throw new Error("Invalid token response from server");
    }

    // Cache the token
    tokenCache = {
      token: data.token,
      region: data.region,
      expiresAt: now + TOKEN_CACHE_DURATION_MS,
    };

    logger.log(
      "[useAzureSpeech] Token fetched successfully for region:",
      data.region
    );

    return { token: data.token, region: data.region };
  } catch (error) {
    logger.error("[useAzureSpeech] Failed to fetch Speech token:", error);
    throw new Error(
      "Could not authenticate with Speech Service. Please try again."
    );
  }
};

export const useAzureSpeech = (): UseAzureSpeechReturn => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Assess pronunciation using Azure Speech Service
   * OPTIMIZED: Receives PushStream directly (PCM data already streamed)
   */
  const assessPronunciation = useCallback(
    async (
      pushStream: SpeechSDK.PushAudioInputStream,
      referenceText: string
    ): Promise<PronunciationResult> => {
      setIsProcessing(true);
      setError(null);

      try {
        logger.log(
          "[useAzureSpeech] Starting pronunciation assessment with PushStream",
          {
            referenceText: referenceText || "(empty - fluency assessment)",
          }
        );

        // 1. Get temporary Speech token (secure - key never exposed to frontend)
        const { token, region } = await getSpeechToken();

        // 2. Configure Speech SDK with authorization token (NOT subscription key)
        const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(
          token,
          region
        );
        // TODO: Make language configurable by git branch.
        speechConfig.speechRecognitionLanguage = "fr-FR";
        speechConfig.outputFormat = SpeechSDK.OutputFormat.Detailed;

        logger.log("[useAzureSpeech] Speech SDK configured with token");

        // 4. Use PushStream directly (already contains PCM data streamed in real-time)
        logger.log(
          "[useAzureSpeech] Using direct PushStream - ZERO latency conversion"
        );

        // 5. Create audio config from stream (PushStream already closed by recorder)
        const audioConfig = SpeechSDK.AudioConfig.fromStreamInput(pushStream);

        // 6. Configure pronunciation assessment
        const pronunciationConfig = new SpeechSDK.PronunciationAssessmentConfig(
          referenceText.trim(),
          SpeechSDK.PronunciationAssessmentGradingSystem.HundredMark,
          SpeechSDK.PronunciationAssessmentGranularity.Word,
          true // Enable miscue detection
        );

        logger.log("[useAzureSpeech] Pronunciation assessment configured");

        // 8. Create recognizer
        const recognizer = new SpeechSDK.SpeechRecognizer(
          speechConfig,
          audioConfig
        );
        pronunciationConfig.applyTo(recognizer);

        logger.log("[useAzureSpeech] Sending to Azure...");

        // 9. Perform recognition
        const result = await new Promise<PronunciationResult>(
          (resolve, reject) => {
            recognizer.recognizeOnceAsync(
              (result) => {
                logger.log("[useAzureSpeech] Azure response received:", {
                  reason: result.reason,
                  text: result.text,
                });

                if (result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
                  try {
                    const jsonResult = result.properties.getProperty(
                      SpeechSDK.PropertyId.SpeechServiceResponse_JsonResult
                    );

                    logger.log("[useAzureSpeech] Raw JSON result:", jsonResult);

                    const parsed: PronunciationResult = JSON.parse(jsonResult);

                    logger.log("[useAzureSpeech] Assessment successful:", {
                      pronScore:
                        parsed.NBest?.[0]?.PronunciationAssessment?.PronScore,
                      recognizedText: parsed.NBest?.[0]?.Display,
                      wordsCount: parsed.NBest?.[0]?.Words?.length,
                    });

                    resolve(parsed);
                  } catch (parseError) {
                    logger.error(
                      "[useAzureSpeech] Error parsing result:",
                      parseError
                    );
                    reject(
                      new Error("Failed to parse pronunciation assessment")
                    );
                  }
                } else if (result.reason === SpeechSDK.ResultReason.NoMatch) {
                  logger.warn("[useAzureSpeech] No speech detected in audio");
                  reject(
                    new Error(
                      "Could not detect speech. Please speak clearly into the microphone."
                    )
                  );
                } else if (result.reason === SpeechSDK.ResultReason.Canceled) {
                  const cancellation =
                    SpeechSDK.CancellationDetails.fromResult(result);
                  logger.error("[useAzureSpeech] Recognition canceled:", {
                    reason: cancellation.reason,
                    errorCode: cancellation.ErrorCode,
                    errorDetails: cancellation.errorDetails,
                  });

                  // Handle specific error codes
                  if (cancellation.errorDetails?.includes("401")) {
                    reject(
                      new Error(
                        "Authentication error. Please check Azure credentials."
                      )
                    );
                  } else if (cancellation.errorDetails?.includes("timeout")) {
                    reject(
                      new Error(
                        "Connection timeout. Please check your internet connection."
                      )
                    );
                  } else {
                    reject(
                      new Error(
                        `Recognition failed: ${cancellation.errorDetails || "Unknown error"}`
                      )
                    );
                  }
                } else {
                  reject(
                    new Error(`Unexpected result reason: ${result.reason}`)
                  );
                }

                recognizer.close();
              },
              (error) => {
                logger.error("[useAzureSpeech] Recognition error:", error);
                reject(
                  new Error(
                    `Azure Speech Service error: ${error || "Unknown error"}`
                  )
                );
                recognizer.close();
              }
            );
          }
        );

        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Assessment failed";
        logger.error("[useAzureSpeech] Error:", errorMessage, err);
        setError(errorMessage);
        throw err;
      } finally {
        setIsProcessing(false);
      }
    },
    []
  );

  return {
    assessPronunciation,
    isProcessing,
    error,
    clearError,
  };
};
